// src/components/Sidebar.tsx
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useData, ModuleKey, Role } from "../DataContext";
import { useTranslation } from "react-i18next";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  authRole: Role;
};

type Item = {
  to: string;
  label: string;
  sub?: string;
  module?: ModuleKey;
  icon?: React.ReactNode;
};

type Section = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  items: Item[];
};

const Icon = ({ children, title }: { children: React.ReactNode; title?: string }) => (
  <span
    aria-hidden="true"
    title={title}
    style={{ display: "inline-flex", width: 18, justifyContent: "center" }}
  >
    {children}
  </span>
);

export default function Sidebar({ isOpen, onClose, onLogout, authRole }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasModule, authSession } = useData();
  const { t } = useTranslation();

  const isAdmin = authRole === "admin";
  const isNjdc = authSession?.tenantId === "njdc";

  const canUse = (m?: ModuleKey) => {
    if (!m) return true;
    return hasModule(m);
  };

  // Shared icons (match TopNav)
  const ICONS = useMemo(
    () => ({
      dashboard: "📊",
      quick: "⭐",
      operations: "🧩",
      setup: "🛠️",
      finance: "💳",
      timepay: "🕒",

      company: "🏢",
      clients: "👤",
      projects: "📁",
      resources: "🧰",
      subcontracts: "🧾",

      moneyOut: "💸",
      moneyIn: "💰",
      partners: "🤝",
      wages: "🕒",
      schedule: "🗓️",
      invoice: "🧾",
      lock: "🔒",
      materials: "🧱",
    }),
    []
  );

  // Drawer groups (accordion)
  const sections: Section[] = useMemo(
    () => [
      {
        key: "quick",
        label: t("sidebar.sections.quickAccess"),
        icon: ICONS.quick,
        items: [
          { to: "/projects", label: t("sidebar.nav.projects"), module: "projects", icon: ICONS.projects },
          { to: "/expenses", label: t("sidebar.nav.moneyOut"), module: "expenses", icon: ICONS.moneyOut },
          { to: "/payments-received", label: t("sidebar.nav.moneyIn"), module: "payments", icon: ICONS.moneyIn },
          { to: "/wages", label: t("sidebar.nav.timePay"), module: "wages", icon: ICONS.wages },
          { to: "/schedule", label: t("sidebar.nav.schedule"), module: "core", icon: ICONS.schedule },
          { to: "/materials", label: t("sidebar.nav.materials"), module: "core", icon: ICONS.materials },
          { to: "/subcontracts", label: t("sidebar.nav.subContracts"), module: "subcontracts", icon: ICONS.subcontracts },
          { to: "/invoices", label: t("sidebar.nav.invoices"), sub: t("sidebar.nav.invoicesSub"), module: "invoices", icon: ICONS.invoice },
        ],
      },
      {
        key: "operations",
        label: t("sidebar.sections.operations"),
        icon: ICONS.operations,
        items: [
          { to: "/projects", label: t("sidebar.nav.projects"), sub: t("sidebar.nav.projectsSub"), module: "projects", icon: ICONS.projects },
          {
            to: "/subcontracts",
            label: t("sidebar.nav.subContracts"),
            sub: t("sidebar.nav.subContractsSub"),
            module: "subcontracts",
            icon: ICONS.subcontracts,
          },
          { to: "/schedule", label: t("sidebar.nav.schedule"), sub: t("sidebar.nav.scheduleSub"), module: "core", icon: ICONS.schedule },
          { to: "/materials", label: t("sidebar.nav.materials"), sub: t("sidebar.nav.materialsSub"), module: "core", icon: ICONS.materials },
        ],
      },
      {
        key: "finance",
        label: t("sidebar.sections.finance"),
        icon: ICONS.finance,
        items: [
          { to: "/invoices", label: t("sidebar.nav.invoices"), sub: t("sidebar.nav.invoicesSub"), module: "invoices", icon: ICONS.invoice },
          { to: "/expenses", label: t("sidebar.nav.moneyOut"), sub: t("sidebar.nav.moneyOutSub"), module: "expenses", icon: ICONS.moneyOut },
          { to: "/payments-received", label: t("sidebar.nav.moneyIn"), sub: t("sidebar.nav.moneyInSub"), module: "payments", icon: ICONS.moneyIn },
          {
            to: "/partner-disbursements",
            label: t("sidebar.nav.partnerDisbursements"),
            sub: t("sidebar.nav.partnerDisbursementsSub"),
            module: "partners",
            icon: ICONS.partners,
          },
        ],
      },
      {
        key: "setup",
        label: t("sidebar.sections.setup"),
        icon: ICONS.setup,
        items: [
          { to: "/my-company", label: t("sidebar.nav.myCompany"), sub: t("sidebar.nav.myCompanySub"), module: "core", icon: ICONS.company },
          { to: "/clients", label: t("sidebar.nav.clients"), sub: t("sidebar.nav.clientsSub"), module: "clients", icon: ICONS.clients },
          { to: "/resources", label: t("sidebar.nav.resources"), sub: t("sidebar.nav.resourcesSub"), module: "resources", icon: ICONS.resources },
        ],
      },
      {
        key: "timepay",
        label: t("sidebar.sections.timePay"),
        icon: ICONS.timepay,
        items: [
          { to: "/wages", label: t("sidebar.nav.wages"), sub: t("sidebar.nav.timePaySub"), module: "wages", icon: ICONS.wages },
        ],
      },
    ],
    [ICONS, t]
  );

  // Accordion behavior: only one open at a time
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  // Auto-open the section that matches the current route
  // ✅ BUT: never auto-open Quick Access (pinned) — stays collapsed by default
  useEffect(() => {
    const path = location.pathname;
    const hit = sections
      .filter((s) => s.key !== "quick")
      .find((s) => s.items.some((it) => path === it.to || path.startsWith(it.to + "/")));
    setOpenGroup(hit?.key ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const go = (to: string) => {
    navigate(to);
    onClose();
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const goReset = () => go("/reset-data");
  const goAdmin = () => go("/admin");

  return (
    <>
      {/* Overlay */}
      <div className={`sidebarOverlay ${isOpen ? "open" : ""}`} onClick={onClose} />

      <aside className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebarHeader">
          <div className="sidebarBrand">
            <div className="sidebarLogo">NJ</div>
            <div>
              <div className="sidebarTitle">NJDC</div>
              <div className="sidebarSub">Operations Console</div>
            </div>
          </div>

          {/* Close button UX: bigger target + consistent icon button */}
          <button
            className="iconBtn sidebarClose"
            aria-label={t("sidebar.close")}
            onClick={onClose}
            type="button"
            title={t("sidebar.close")}
          >
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1, fontWeight: 900 }}>
              ×
            </span>
          </button>
        </div>

        <div className="sidebarBody">
          {/* Dashboard (single item) */}
          {(() => {
            const enabled = canUse("core");
            const active = location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/");
            if (!enabled) {
              return (
                <button
                  className={`navItem navItemButton ${active ? "navItemActive" : ""}`}
                  type="button"
                  disabled
                  title={`${t("sidebar.locked")} — v1.1.0: Spanish/English toggle, Client Decisions tab, Schedule improvements, unit tests`}
                  style={{ opacity: 0.6, cursor: "not-allowed" }}
                >
                  <Icon>{ICONS.dashboard}</Icon>
                  <span style={{ marginLeft: 8 }}>{t("sidebar.dashboard")}</span>
                  <span style={{ marginLeft: 8 }}>{ICONS.lock}</span>
                </button>
              );
            }
            return (
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `navItem ${isActive ? "navItemActive" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  go("/dashboard");
                }}
              >
                <Icon>{ICONS.dashboard}</Icon>
                <span style={{ marginLeft: 8 }}>{t("sidebar.dashboard")}</span>
              </NavLink>
            );
          })()}

          <div className="sidebarDivider" />

          {/* Accordion groups */}
          {sections.map((sec) => {
            const expanded = openGroup === sec.key;

            return (
              <div className="navSection" key={sec.key}>
                <button
                  type="button"
                  className="navItem navItemButton"
                  onClick={() => setOpenGroup((prev) => (prev === sec.key ? null : sec.key))}
                  style={{
                    fontWeight: 900,
                    justifyContent: "space-between",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                  title={sec.label}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Icon>{sec.icon ?? "•"}</Icon>
                    <span style={{ fontSize: 12, letterSpacing: 1.2, color: "var(--muted)" }}>
                      {sec.label.toUpperCase()}
                    </span>
                  </span>
                  <span aria-hidden="true">{expanded ? "—" : "＋"}</span>
                </button>

                {expanded ? (
                  <div className="navItems" style={{ marginTop: 6 }}>
                    {sec.items.map((it) => {
                      const enabled = canUse(it.module);
                      const active = location.pathname === it.to || location.pathname.startsWith(it.to + "/");

                      if (!enabled) {
                        return (
                          <button
                            key={it.to}
                            className={`navItem navItemButton ${active ? "navItemActive" : ""}`}
                            type="button"
                            disabled
                            title={`${t("sidebar.locked")} — v1.1.0: Spanish/English toggle, Client Decisions tab, Schedule improvements, unit tests`}
                            style={{ opacity: 0.6, cursor: "not-allowed" }}
                          >
                            <Icon>{it.icon ?? "•"}</Icon>
                            <span style={{ marginLeft: 8 }}>{it.label}</span>
                            <span style={{ marginLeft: 8 }}>{ICONS.lock}</span>
                          </button>
                        );
                      }

                      return (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          className={({ isActive }) => `navItem ${isActive ? "navItemActive" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            go(it.to);
                          }}
                          title={it.sub || it.label}
                        >
                          <Icon>{it.icon ?? "•"}</Icon>
                          <span style={{ marginLeft: 8 }}>{it.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}

          <div className="sidebarDivider" />

          {/* Quick Start */}
          <NavLink
            to="/quick-start"
            className={({ isActive }) => `navItem ${isActive ? "navItemActive" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              go("/quick-start");
            }}
            title={t("sidebar.quickStart")}
          >
            <Icon>🚀</Icon>
            <span style={{ marginLeft: 8 }}>{t("sidebar.quickStart")}</span>
          </NavLink>

          {/* Translation Review — NJDC tenant only */}
          {isNjdc && (
            <NavLink
              to="/translation-review"
              className={({ isActive }) => `navItem ${isActive ? "navItemActive" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                go("/translation-review");
              }}
              title="Translation Review"
            >
              <Icon>🌐</Icon>
              <span style={{ marginLeft: 8 }}>Translation Review</span>
            </NavLink>
          )}

          <div className="sidebarDivider" />

          {/* System */}
          <div className="navSection sidebarSystemBlock">
            <div className="navSectionTitle staticTitle">{t("sidebar.system")}</div>

            {isAdmin ? (
              <div className="sidebarSystemActions">
                <button className="navItem navItemButton" onClick={goAdmin} type="button">
                  {t("sidebar.admin")}
                </button>

                {/* Gap between Admin & Reset/Clean is handled by .sidebarSystemActions (CSS) */}
                <button className="navItem navItemButton" onClick={goReset} type="button">
                  {t("sidebar.resetData")}
                </button>
              </div>
            ) : null}

            <button
              className="navItem navItemDanger sidebarLogout"
              onClick={() => {
                onClose();
                onLogout();
              }}
              type="button"
            >
              {t("sidebar.logout")}
            </button>
          </div>
        </div>

        <div style={{ padding: "10px 16px", color: "var(--muted)", fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>
          v1.1.0
        </div>
      </aside>
    </>
  );
}
